import { useCallback, useEffect, useState } from "react";
import {
  SeatmapOverview,
  SeatmapUpdate,
} from "@egocentric-systems/ts-apis/booking_gateway/v1/updates_pb";
import { PlainMessage } from "@bufbuild/protobuf";
import { EventOrBundle } from "~/lib/definitions";

type EventId = string;
type SectionId = string;

function parseOverview(
  eventData: any,
): Omit<PlainMessage<SeatmapOverview>, "event"> {
  try {
    return SeatmapOverview.fromJsonString(
      eventData,
    ) as PlainMessage<SeatmapOverview>;
  } catch {
    return {
      gaAvailability: {},
      seatAvailability: {},
    };
  }
}

function parseUpdate(
  eventData: any,
): Omit<PlainMessage<SeatmapUpdate>, "event"> {
  try {
    return SeatmapUpdate.fromJsonString(
      eventData,
    ) as PlainMessage<SeatmapUpdate>;
  } catch {
    return {
      gaAvailability: {},
      seatUpdates: {},
    };
  }
}

interface Options {
  enabled: boolean;
}

export function useSeatmapUpdates(
  eventOrBundle: EventOrBundle,
  sections: Record<string | number, string[]>,
  { enabled }: Options,
): {
  availableSeats: Record<EventId, number[]>;
  isSectionGaAndBookedOut: (sectionId?: string | number) => boolean;
} {
  const [availableSeats, setAvailableSeats] = useState<
    Record<EventId, number[]>
  >({});
  const [eventAndSectionAvailablility, setEventAndSectionAvailablility] =
    useState<Record<EventId, Record<SectionId, number>>>({});

  const setupEventUpdates = useCallback((eventId: string): EventSource => {
    const eventSource = new EventSource(
      `${process.env.NEXT_PUBLIC_API_GATEWAY}/booking_gateway.v1.Updates/seatmap/${eventId}`,
    );

    eventSource.addEventListener("SeatmapOverview", (event) => {
      const inititalAvailibility = parseOverview(event.data);

      const initialAvailableSeats = Object.values(
        inititalAvailibility.seatAvailability,
      ).flatMap((section) => section.ids);

      setAvailableSeats((oldAvailableSeats) => ({
        ...oldAvailableSeats,
        [eventId]: initialAvailableSeats,
      }));

      setEventAndSectionAvailablility((oldAvailability) => ({
        ...oldAvailability,
        [eventId]: inititalAvailibility.gaAvailability,
      }));
    });

    eventSource.addEventListener("SeatmapUpdate", (event) => {
      const availabilityUpdate = parseUpdate(event.data);

      const updates = Object.values(availabilityUpdate.seatUpdates);

      const newReserved = updates.flatMap((section) => section.reserved);
      const newAvailable = updates.flatMap((section) => section.available);

      setAvailableSeats((oldAvailableSeats) => ({
        ...oldAvailableSeats,
        [eventId]: [
          ...oldAvailableSeats[eventId].filter(
            (seat) => !newReserved.includes(seat),
          ),
          ...newAvailable,
        ],
      }));

      setEventAndSectionAvailablility((oldAvailability) => ({
        ...oldAvailability,
        [eventId]: availabilityUpdate.gaAvailability,
      }));
    });

    return eventSource;
  }, []);

  const setupBundleUpdates = useCallback(
    (eventIds: string[]): EventSource[] => {
      return eventIds.map((eventId) => setupEventUpdates(eventId));
    },
    [setupEventUpdates],
  );

  useEffect(() => {
    if (!enabled || !eventOrBundle.value?.id) {
      return;
    }

    const eventSources =
      eventOrBundle.case === "event"
        ? [setupEventUpdates(eventOrBundle.value.id)]
        : setupBundleUpdates(eventOrBundle.value.eventIds);

    return () => {
      for (const eventSource of eventSources) {
        eventSource.close();
      }
    };
  }, [eventOrBundle, enabled, setupEventUpdates, setupBundleUpdates]);

  const isSectionGaAndBookedOut = useCallback(
    (sectionId?: string | number) => {
      if (!sectionId) {
        return false;
      }

      return Object.values(eventAndSectionAvailablility).some(
        (sectionAvailablility) =>
          sections[sectionId]?.some((mappedSection) => {
            return sectionAvailablility[mappedSection] === 0;
          }),
      );
    },
    [eventAndSectionAvailablility, sections],
  );

  return {
    availableSeats,
    isSectionGaAndBookedOut,
  };
}
